/**
 * Appels de la séance (`ARCHITECTURE_V2.md` §4.1, §4.2 et §4.5, specs §12.3).
 *
 * 🔴 **Adressées par `clientId` à tous les étages** (§2.3-b) : la séance, chaque
 * série, chaque note. C'est ce qui permet à une séance entière de se composer
 * hors ligne **sans un seul place-titre `{{op:n}}`** et sans déclaration de
 * dépendance — le client connaît déjà toutes les identités qu'il manipule, donc
 * il n'attend rien du serveur pour écrire la suite.
 *
 * 🔴 **Que des `PUT` et des `DELETE`, jamais un `PATCH`** — cf. l'en-tête de
 * `server/src/routes/sessions.ts`. Ce n'est pas une symétrie décorative : un
 * `PATCH` sur une série dont le `PUT` n'a pas encore été drainé répondrait 404,
 * donc `failed` définitif (§3.3), c'est-à-dire **une série perdue en silence au
 * milieu d'une séance**. Chaque écriture d'ici porte la ressource entière.
 *
 * ⚠️ **`crypto.randomUUID()` et rien d'autre** (`newClientId`), comme pour les
 * exercices et les routines.
 */

import type {
  ActualSetInput,
  ExerciseRecallDTO,
  LastPassageDTO,
  SessionInput,
  SessionNoteInput,
  SessionPlanInput,
  WorkoutSessionDTO,
} from '../../../shared/types';
import { apiFetch } from './client';

/**
 * Clé de cache des séances récentes.
 *
 * 🔴 **Une fenêtre, pas la réplique intégrale — la seule table de la V2 qui
 * n'est pas répliquée en entier.** Exercices et routines le sont parce que leur
 * volume est borné par nature ; une séance s'ajoute quatre fois par semaine,
 * soit ≈ 1 000 à cinq ans (§6.1). Le serveur en rend les **30 plus récentes**
 * (≈ 90 Ko, l'ordre de grandeur de la base personnelle d'aliments), ce qui
 * couvre deux mois — et la séance en cours en fait partie par construction,
 * puisqu'elle a commencé aujourd'hui.
 *
 * ⚠️ **Une seule entrée, sans terme ni plage dans la clé**, comme les trois
 * autres domaines : c'est la leçon de l'étape 4 (`lib/localSearch.ts`).
 */
export const SESSIONS_KEY = ['sessions'] as const;

/**
 * Clé d'une séance précise.
 *
 * ⚠️ **Distincte de `SESSIONS_KEY`, et les deux sont persistées.** La liste
 * borne son volume à trente ; celle-ci garde la séance qu'on est en train de
 * faire, qui doit survivre à une fermeture de l'application **même si elle
 * n'a jamais atteint le serveur** — c'est très exactement le critère du §8.6.
 */
export function sessionKey(clientId: string) {
  return ['session', clientId] as const;
}

/**
 * Clé du dernier passage sur un exercice.
 *
 * ⚠️ **Une clé par exercice**, préchargée à l'ouverture de la séance (§4.5) :
 * c'est en salle qu'on en a besoin, c'est-à-dire là où il n'y a pas de réseau.
 * La politique exacte des images et du détail d'un exercice.
 */
export function lastPassageKey(exerciseClientId: string, exclude?: string, routine?: string) {
  /*
   * ⚠️ **La séance exclue fait partie de la clé.** Deux séances différentes
   * n'ont pas le même « dernier passage » sur un exercice : partager l'entrée
   * ferait servir à l'une le rappel calculé pour l'autre.
   *
   * ⚠️ **Et la routine aussi, depuis l'étape 7.** Elle est *en pratique*
   * déduite de la séance — mais la réponse en dépend désormais (c'est elle qui
   * porte l'incrément de progression), et une clé de cache qui omet une entrée
   * de sa fonction est un cache qui sert la réponse d'une autre question. La
   * règle de TanStack Query, pas une précaution de style.
   */
  return ['exercise-last', exerciseClientId, exclude ?? '', routine ?? ''] as const;
}

export function fetchSessions(): Promise<WorkoutSessionDTO[]> {
  return apiFetch<WorkoutSessionDTO[]>('/api/sessions');
}

export function fetchSession(clientId: string): Promise<WorkoutSessionDTO> {
  return apiFetch<WorkoutSessionDTO>(`/api/sessions/${encodeURIComponent(clientId)}`);
}

/**
 * Le rappel d'un exercice : son dernier passage, **et la charge proposée**.
 *
 * 🔴 **Un seul appel pour les deux, et c'est la décision de l'étape 7.** La
 * proposition de progression a besoin d'exactement ce que cet appel rapporte
 * déjà, et elle est nécessaire au même endroit — devant la barre, en salle,
 * sans réseau. Lui donner son propre appel aurait créé un second chemin à
 * précharger, à persister et à faire échouer proprement, pour rien. Elle
 * hérite ici du préchargement de l'étape 6 et de la racine `exercise-last` de
 * `lib/persist.ts`, donc du hors ligne, sans une ligne de plus.
 *
 * ⚠️ **`passage: null` est une réponse, pas une erreur** : l'exercice existe,
 * la question est valide, et « premier passage » est un cas nominal que l'écran
 * dit en toutes lettres. La progression, elle, a **quand même** quelque chose à
 * dire dans ce cas — la charge de départ de la routine (§4.4).
 */
export function fetchLastPassage(
  exerciseClientId: string,
  /**
   * La séance en cours, pour qu'elle ne devienne pas son propre « dernier
   * passage ».
   *
   * ⚠️ **Exclue par son IDENTITÉ, pas parce qu'elle est ouverte.** La première
   * version écartait toute séance non terminée côté serveur : elle excluait la
   * séance du jour, mais aussi **toutes celles qu'on avait oublié de clore** —
   * et le rappel restait vide alors que le travail avait bien eu lieu.
   */
  exclude?: string,
  /**
   * La routine dont on veut la **prescription** de progression.
   *
   * ⚠️ **Distincte d'`exclude`, alors que la séance connaît sa routine.** Faire
   * dire deux choses à un champ nommé « exclure » est ce que le projet refuse
   * partout ailleurs. Absente — une fiche ouverte depuis la bibliothèque —, le
   * serveur ne calcule aucune progression et rend le rappel entier.
   */
  routine?: string,
): Promise<ExerciseRecallDTO> {
  const params = new URLSearchParams();
  if (exclude !== undefined) params.set('exclude', exclude);
  if (routine !== undefined) params.set('routine', routine);
  const query = params.size === 0 ? '' : `?${params.toString()}`;
  return apiFetch<ExerciseRecallDTO>(
    `/api/exercises/${encodeURIComponent(exerciseClientId)}/last${query}`,
  );
}

/**
 * Clé de l'historique d'un exercice.
 *
 * ⚠️ **Distincte du dernier passage**, qui porte la séance exclue dans sa clé.
 * L'historique, lui, ne dépend que de l'exercice : on le lit depuis une fiche,
 * pas depuis une séance.
 */
export function exerciseHistoryKey(exerciseClientId: string) {
  return ['exercise-history', exerciseClientId] as const;
}

/**
 * Les passages précédents sur un exercice, du plus récent au plus ancien.
 *
 * ⚠️ **Le serveur borne à trente séances** et ne fait aucune agrégation : il
 * rend les séries telles qu'elles ont été faites, et c'est l'écran qui en tire
 * la charge du jour. Agréger côté serveur figerait un choix que seule la
 * lecture peut faire (§6.1 : rien n'est matérialisé).
 */
export function fetchExerciseHistory(exerciseClientId: string): Promise<LastPassageDTO[]> {
  return apiFetch<LastPassageDTO[]>(
    `/api/exercises/${encodeURIComponent(exerciseClientId)}/history`,
  );
}

/**
 * Pose le plan de la séance — **une seule opération**.
 *
 * 🔴 **Elle insère ce qui manque et ne réécrit jamais ce qui existe.** C'est ce
 * qui remplace les trente `putSet` du premier jet, lesquels faisaient attendre
 * ≈ 25 s avant l'ouverture de l'écran.
 */
export function putSessionPlan(
  clientId: string,
  body: SessionPlanInput,
): Promise<WorkoutSessionDTO> {
  return apiFetch<WorkoutSessionDTO>(`/api/sessions/${encodeURIComponent(clientId)}/plan`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * L'en-tête de la séance — création **ou** remplacement.
 *
 * ⚠️ **Ne porte pas les séries**, et c'est le cœur du découpage : son rejeu par
 * la file ne peut donc pas réécrire le plan initial par-dessus des séries déjà
 * faites.
 */
export function putSession(clientId: string, body: SessionInput): Promise<WorkoutSessionDTO> {
  return apiFetch<WorkoutSessionDTO>(`/api/sessions/${encodeURIComponent(clientId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteSession(clientId: string): Promise<void> {
  return apiFetch<void>(`/api/sessions/${encodeURIComponent(clientId)}`, { method: 'DELETE' });
}

/**
 * Une série entière.
 *
 * 🔴 **La même écriture sert à poser le plan et à enregistrer le résultat**, ce
 * qui rend l'ordre de drainage indifférent : valider une série dont le plan n'a
 * jamais été transmis l'écrit simplement pour la première fois.
 *
 * ⚠️ **Renvoie la séance entière**, comme toute mutation de journal renvoie la
 * journée entière (étape 5) : le front réconcilie son aperçu optimiste en un
 * aller-retour, plutôt que de recoller une ligne dans un arbre.
 */
export function putSet(
  clientId: string,
  setClientId: string,
  body: ActualSetInput,
): Promise<WorkoutSessionDTO> {
  return apiFetch<WorkoutSessionDTO>(
    `/api/sessions/${encodeURIComponent(clientId)}/sets/${encodeURIComponent(setClientId)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

/**
 * ⚠️ **Ce n'est pas le chemin normal.** Retirer une série d'une séance en
 * cours, c'est `status: 'skipped'` (§4.5) — on veut savoir qu'elle était
 * prévue. Cette route corrige une série ajoutée par erreur.
 */
export function deleteSet(clientId: string, setClientId: string): Promise<void> {
  return apiFetch<void>(
    `/api/sessions/${encodeURIComponent(clientId)}/sets/${encodeURIComponent(setClientId)}`,
    { method: 'DELETE' },
  );
}

export function putSessionNote(
  clientId: string,
  exerciseClientId: string,
  body: SessionNoteInput,
): Promise<WorkoutSessionDTO> {
  return apiFetch<WorkoutSessionDTO>(
    `/api/sessions/${encodeURIComponent(clientId)}/notes/${encodeURIComponent(exerciseClientId)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

export function deleteSessionNote(clientId: string, exerciseClientId: string): Promise<void> {
  return apiFetch<void>(
    `/api/sessions/${encodeURIComponent(clientId)}/notes/${encodeURIComponent(exerciseClientId)}`,
    { method: 'DELETE' },
  );
}
